import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { theme } from './theme';
import { suppressResizeObserverError } from './utils/suppressResizeObserverError';
import { getCurrentEnvironment } from './utils/environment';

// Suppress ResizeObserver errors as early as possible
suppressResizeObserverError();

// Detect if we're running on localhost or development domains
const isDevelopment = window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname === 'horwathportal.local' ||
                     window.location.hostname.endsWith('.local');

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: isDevelopment ? false : 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Function to get config value
function getConfig(key: string): string {
  return (window as any).ENV?.[key] || import.meta.env[key] || '';
}

// Function to get redirect URI
function getRedirectUri(): string {
  // Special case: Local development with horwathportal.local domain
  if (window.location.hostname === 'horwathportal.local') {
    return `${window.location.protocol}//${window.location.host}`;
  }

  // For all other environments, use the configured redirect URI
  const configuredUri = getConfig('VITE_REDIRECT_URI');
  if (configuredUri) {
    return configuredUri;
  }

  // Fallback: use current origin
  return window.location.origin;
}

// Function to create MSAL configuration
function createMsalConfig(): Configuration {
  const currentEnvironment = getCurrentEnvironment();
  const clientId = getConfig('VITE_AZURE_CLIENT_ID');

  console.log('Creating MSAL config with:', {
    clientId,
    redirectUri: getRedirectUri(),
    environment: currentEnvironment.displayName
  });

  if (!clientId) {
    console.error('CRITICAL: VITE_AZURE_CLIENT_ID is not set! Check your config.js file.');
  }

  return {
    auth: {
      clientId: clientId || 'missing-client-id', // Use a placeholder to prevent empty string
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: getRedirectUri(),
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;

          const envPrefix = `[MSAL-${currentEnvironment.displayName}]`;
          const logMessage = `${envPrefix} ${message}`;

          switch (level) {
            case LogLevel.Error:
              console.error(logMessage);
              return;
            case LogLevel.Info:
              // Suppress Info logs in all environments
              return;
            case LogLevel.Verbose:
              // Suppress Verbose logs in all environments
              return;
            case LogLevel.Warning:
              console.warn(logMessage);
              return;
          }
        },
      },
    },
  };
}

// Initialize and render app
async function initializeApp() {
  try {
    // Wait a bit to ensure config script is loaded
    if (!(window as any).ENV) {
      console.log('Waiting for configuration to load...');
      await new Promise(resolve => {
        const checkConfig = setInterval(() => {
          if ((window as any).ENV) {
            clearInterval(checkConfig);
            resolve(true);
          }
        }, 100);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkConfig);
          console.error('Configuration failed to load after 5 seconds');
          resolve(false);
        }, 5000);
      });
    }

    // Create MSAL instance with loaded config
    const msalConfig = createMsalConfig();
    const msalInstance = new PublicClientApplication(msalConfig);

    // Add event callback to handle auth errors
    msalInstance.addEventCallback((event: any) => {
      if (event.eventType === 'LOGIN_FAILURE' || event.eventType === 'ACQUIRE_TOKEN_FAILURE') {
        const error = event.error;

        // Check for specific authorization errors indicating AAD app needs approval
        if (error?.errorCode === 'consent_required' ||
            error?.errorCode === 'interaction_required' ||
            error?.errorCode === 'admin_consent_required' ||
            error?.errorMessage?.includes('AADSTS65001') || // Consent required
            error?.errorMessage?.includes('AADSTS50076') || // MFA required but app not approved
            error?.errorMessage?.includes('AADSTS90094') || // Admin consent required
            error?.errorMessage?.includes('AADSTS90095') || // Admin has not consented
            error?.errorMessage?.includes('AADSTS65004') || // User declined consent
            error?.errorMessage?.includes('AADSTS700003')) { // Device auth required but not approved
          // Redirect to authorization needed page
          window.location.href = '/authorization-needed';
        }
      }
    });

    // Initialize MSAL
    await msalInstance.initialize();
    console.log('MSAL initialized successfully');

    // Handle redirect promise on page load
    const tokenResponse = await msalInstance.handleRedirectPromise();
    if (tokenResponse) {
      console.log('Token response received:', tokenResponse.account?.username);
    }

    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    // Add global styles to ensure proper layout
    const rootElement = document.getElementById('root') as HTMLElement;
    if (rootElement) {
      rootElement.style.height = '100%';
      rootElement.style.display = 'flex';
      rootElement.style.flexDirection = 'column';
    }

    // Also ensure html and body have proper height
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.display = 'flex';
    document.body.style.flexDirection = 'column';

    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <MsalProvider instance={msalInstance}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Router>
                <App />
              </Router>
            </ThemeProvider>
          </MsalProvider>
        </QueryClientProvider>
      </React.StrictMode>
    );
  } catch (error: any) {
    console.error('Error during app initialization:', error);

    // Check for specific authorization errors indicating AAD app needs approval
    if (error?.errorCode === 'consent_required' ||
        error?.errorCode === 'interaction_required' ||
        error?.errorCode === 'admin_consent_required' ||
        error?.errorMessage?.includes('AADSTS65001') || // Consent required
        error?.errorMessage?.includes('AADSTS50076') || // MFA required but app not approved
        error?.errorMessage?.includes('AADSTS90094') || // Admin consent required
        error?.errorMessage?.includes('AADSTS90095')) { // Admin has not consented
      // Redirect to authorization needed page
      window.location.href = '/authorization-needed';
      return;
    }
  }
}

// Start the app
initializeApp();